import { useMutation } from '@apollo/client';
import { useEffect } from 'react';
import { Trans } from 'react-i18next';
import { useDispatch } from 'react-redux';
import { moveListsAndFoldersMutation } from 'api/mutations';

import { ListId } from 'modules/domains';
import { FolderId } from 'modules/domains/foldering';
import { useTranslation } from 'modules/segmentation/hooks';
import { addNegativeToastWithErrorId, addPositiveToast } from 'modules/toastContainer';
import { NamedFolder, ResponseWrapper } from 'ui/toast';
import noop from 'utils/noop';
import { CurrentSelection, SelectionTypes } from 'utils/contexts/selection';

export enum KnownMoveFailures {
  DestinationNotFoundFailure = 'DestinationNotFoundFailure',
  FolderIsAncestorOfDestinationFailure = 'FolderIsAncestorOfDestinationFailure',
  FolderNameConflictFailure = 'FolderNameConflictFailure',
  FolderNameNotFoundFailure = 'FolderNameNotFoundFailure',
  InternalError = 'InternalError',
  ParentNotFoundFailure = 'ParentNotFoundFailure',
}

export type MoveError = {
  id: FolderId | ListId;
  name?: string;
  __typename: KnownMoveFailures;
};

export type MovedSuccess = {
  id: FolderId;
  name: string;
};

export type MoveFolderResponseData = {
  moveListContentsAndFolders: {
    errors: MoveError[];
    movedContent: MovedSuccess[];
  };
};

export type Variables = {
  destinationId: FolderId | null;
  folderIds: FolderId[];
  contentIds: ListId[];
};

export type Config = {
  onComplete: () => void;
  onError: () => void;
  onSuccess: (data?: MoveFolderResponseData | null) => void;
  destinationName: string;
  itemsById: Record<FolderId | ListId, CurrentSelection>;
};
export function useMoveListsAndFoldersMutation({
  onComplete = noop,
  onError = noop,
  onSuccess = noop,
  destinationName,
  itemsById = {},
}: Config) {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const [moveListsAndFolders, { called, loading, error, data }] = useMutation<
    MoveFolderResponseData,
    Variables
  >(moveListsAndFoldersMutation);
  useEffect(() => {
    // don't do anything until the mutation gets called
    if (!called) {
      return;
    }

    if (!loading && error) {
      const errorMessage = t('indexPage.toasts.moveListContentsAndFolders.error.unknown');
      dispatch(addNegativeToastWithErrorId(errorMessage, 'useMoveListsAndFolders', error));
    }

    if (!loading && data?.moveListContentsAndFolders) {
      const { errors, movedContent } = data.moveListContentsAndFolders;

      errors.forEach((error) => {
        switch (error.__typename) {
          case KnownMoveFailures.DestinationNotFoundFailure: {
            const errorMessage = (
              <ResponseWrapper>
                <Trans
                  t={t}
                  i18nKey="indexPage.toasts.moveListContentsAndFolders.error.destinationNotFound"
                  components={{
                    namedFolder: <NamedFolder name={itemsById?.[error?.id]?.name || ''} />,
                    newLine: <br />,
                    destinationFolder: <NamedFolder name={destinationName} />,
                  }}
                />
                <NamedFolder name={itemsById?.[error?.id]?.name || ''} /> folder can not be moved as{' '}
                <NamedFolder name={destinationName} /> does not exist.
              </ResponseWrapper>
            );

            dispatch(addNegativeToastWithErrorId(errorMessage, 'useMoveListsAndFolders', error));
            break;
          }

          case KnownMoveFailures.FolderNameConflictFailure: {
            const errorMessage = (
              <ResponseWrapper>
                <Trans
                  t={t}
                  i18nKey="indexPage.toasts.moveListContentsAndFolders.error.folderNameConflict"
                  components={{
                    destinationFolder: <NamedFolder name={destinationName} />,
                  }}
                >
                  The folder can not be moved into <NamedFolder name={destinationName} /> as a
                  folder with that name already exists. This could also be because the folder is
                  already a child folder of <NamedFolder name={destinationName} />.
                </Trans>
              </ResponseWrapper>
            );

            dispatch(
              addNegativeToastWithErrorId(errorMessage, 'useMoveCampaignsAndFolders', error),
            );
            break;
          }

          case KnownMoveFailures.FolderIsAncestorOfDestinationFailure: {
            const name = itemsById?.[error?.id]?.name || '';

            const errorMessage = // folder is ancestor of destination failure
              (
                <ResponseWrapper>
                  <Trans
                    t={t}
                    i18nKey="indexPage.toasts.moveListContentsAndFolders.error.folderIsAncestorOfDestination"
                    components={{
                      namedFolder: <NamedFolder name={name} />,
                      newLine: <br />,
                    }}
                  >
                    <NamedFolder name={name} /> folder can not be moved into{' '}
                    <NamedFolder name={name} /> folder or one of its child folders.
                  </Trans>
                </ResponseWrapper>
              );

            dispatch(addNegativeToastWithErrorId(errorMessage, 'useMoveListsAndFolders', error));
            break;
          }

          case KnownMoveFailures.FolderNameNotFoundFailure: {
            const errorMessage = t(
              'indexPage.toasts.moveListContentsAndFolders.error.folderNameNotFound',
            ); // folder name not found
            dispatch(addNegativeToastWithErrorId(errorMessage, 'useMoveListsAndFolders', error));
            break;
          }

          case KnownMoveFailures.ParentNotFoundFailure: {
            const errorMessage = t(
              'indexPage.toasts.moveListContentsAndFolders.error.parentNotFound',
            ); // parent not found
            dispatch(addNegativeToastWithErrorId(errorMessage, 'useMoveListsAndFolders', error));

            break;
          }

          default: {
            const name = itemsById?.[error?.id]?.name || '';
            const errorMessage = (
              <ResponseWrapper>
                <Trans
                  t={t}
                  i18nKey="indexPage.toasts.moveListContentsAndFolders.error.default"
                  components={{
                    namedFolder: <NamedFolder name={name} />,
                  }}
                >
                  <NamedFolder name={name} /> folder could not be moved.
                </Trans>
              </ResponseWrapper>
            );

            dispatch(addNegativeToastWithErrorId(errorMessage, 'useMoveListsAndFolders', error));
          }
        }
        onError();
      });

      movedContent.forEach((content: MovedSuccess) => {
        switch (itemsById[content.id].type) {
          case SelectionTypes.Content: {
            dispatch(
              addPositiveToast(
                <ResponseWrapper>
                  <Trans
                    t={t}
                    i18nKey="indexPage.toasts.moveListContentsAndFolders.success.content"
                    components={{
                      strong: <strong />,
                      newLine: <br />,
                      destinationFolder: <NamedFolder name={destinationName} />,
                    }}
                    variables={{
                      name: content.name,
                    }}
                  >
                    <strong>List {content.name}</strong> was successfully moved into the{' '}
                    <NamedFolder name={destinationName} /> folder.
                  </Trans>
                </ResponseWrapper>,
              ),
            );
            break;
          }

          case SelectionTypes.Folder: {
            dispatch(
              addPositiveToast(
                <ResponseWrapper>
                  <Trans
                    t={t}
                    i18nKey="indexPage.toasts.moveListContentsAndFolders.success.folder"
                    components={{
                      namedFolder: <NamedFolder name={content.name} />,
                      destinationFolder: <NamedFolder name={destinationName} />,
                      newLine: <br />,
                    }}
                  >
                    <NamedFolder name={content.name} /> was successfully moved into the{' '}
                    <NamedFolder name={destinationName} /> folder.
                  </Trans>
                </ResponseWrapper>,
              ),
            );
            break;
          }

          default: {
            console.error('Moved an unallowed content item: possible type mismatch'); // eslint-disable-line
          }
        }

        onSuccess(data);
      });

      // call the complete callback
      onComplete();
    }
  }, [loading]);

  return [moveListsAndFolders, data] as const;
}
